import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFacetPolicyTranslation } from './facetPolicyI18n';

function binding(draft) {
  const numeric = draft && draft.value_policy ? draft.value_policy.numeric : null;
  return JSON.stringify({
    facet_key: draft && draft.facet_key,
    type: draft && draft.type,
    numeric_definition_revision: numeric && numeric.numeric_definition_revision,
  });
}

export default function NumberFacetPolicyEditor({
  api, facetKey, draft, effect, onValidationChange, onRequestReindex,
}) {
  const t = useFacetPolicyTranslation();
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const sequence = useRef(0);
  const draftBinding = useMemo(() => binding({ ...draft, facet_key: facetKey }), [draft, facetKey]);

  useEffect(() => {
    sequence.current += 1;
    setResult(null);
    setStatus('idle');
    setError(null);
    onValidationChange(null);
    return () => { sequence.current += 1; };
  }, [draftBinding, onValidationChange]);

  const validate = async () => {
    sequence.current += 1;
    const current = sequence.current;
    setStatus('loading'); setError(null); setResult(null); onValidationChange(null);
    try {
      const response = await api.validateType({ facet_key: facetKey, target_type: 'number' });
      if (sequence.current !== current) return;
      const data = response && response.data ? response.data : {};
      const valid = data.applicable === true
        && typeof data.validation_token === 'string' && data.validation_token !== '';
      setResult(data);
      setStatus(valid ? 'valid' : 'invalid');
      if (valid) onValidationChange({
        token: data.validation_token,
        catalogRevision: data.catalog_revision,
        binding: draftBinding,
      });
      else onValidationChange(null);
    } catch (validationError) {
      if (sequence.current !== current) return;
      setStatus('stale');
      setError(t(
        'facets.policy.number.error.validation',
        validationError && validationError.message
          ? validationError.message : 'Numeric validation expired. Revalidate.',
      ));
      onValidationChange(null);
    }
  };
  const reindexRequired = effect === 'reindex_required'
    || effect === 'after_reindex'
    || (effect && (effect.requires_reindex === true
      || effect.timing === 'after_reindex'
      || effect.state === 'pending_reindex'));
  const acceptedExclusions = status === 'valid' && Number(result && result.invalid) > 0;

  return (
    <section className="fp-number-editor" aria-label={t('facets.policy.number.validation', 'Numeric facet validation')}>
      {!result && status !== 'loading' && (
        <div className="fp-validation-empty">
          <span aria-hidden="true">#</span>
          <h3>{t('facets.policy.number.checkValues', 'Check catalog values')}</h3>
          <p>{t('facets.policy.number.checkHint', 'We will inspect the catalog before this filter becomes a number range.')}</p>
          <button type="button" className="fp-button fp-button--primary" disabled={!api} onClick={validate}>
            {status === 'stale'
              ? t('facets.policy.number.revalidate', 'Check again')
              : t('facets.policy.number.validate', 'Check values')}
          </button>
          {error && <div className="fp-production-notice is-error" role="alert">{error}</div>}
        </div>
      )}
      {status === 'loading' && (
        <div className="fp-validation-empty is-loading" role="status">
          <span className="fp-spinner" />
          <h3>{t('facets.policy.number.validating', 'Checking values…')}</h3>
        </div>
      )}
      {result && (
        <div aria-label={t('facets.policy.number.result', 'Numeric validation result')}>
          <div className="fp-report-cards">
            <article><span>{t('facets.policy.number.valid', 'Valid values')}</span><strong>{Number(result.valid || 0).toLocaleString()}</strong><small>{t('facets.policy.number.ofTotal', 'of {total}', { total: Number(result.total || 0).toLocaleString() })}</small></article>
            <article className={Number(result.invalid || 0) > 0 ? 'is-problem' : ''}><span>{acceptedExclusions ? t('facets.policy.number.excluded', 'Excluded from range') : t('facets.policy.number.invalid', 'Need attention')}</span><strong>{Number(result.invalid || 0).toLocaleString()}</strong><small>{t('facets.policy.number.values', 'values')}</small></article>
            <article><span>{t('facets.policy.number.range', 'Range')}</span><strong>{result.min == null || result.max == null ? '—' : `${result.min} – ${result.max}`}</strong><small>{t('facets.policy.number.detected', 'detected')}</small></article>
          </div>
          {Number(result.invalid || 0) > 0 && (
            <div className="fp-invalid-values" role={acceptedExclusions ? 'status' : 'alert'} aria-label={t('facets.policy.number.invalidExamples', 'Invalid numeric examples')}>
              <header><span><strong>{acceptedExclusions ? t('facets.policy.number.excludedHeading', 'Some values are excluded from the range') : t('facets.policy.number.invalidHeading', 'Some values are not numeric')}</strong><small>{acceptedExclusions ? t('facets.policy.number.excludedHint', 'Excluded values do not participate in the numeric filter. Products and valid values are kept. You can save this policy.') : t('facets.policy.number.invalidHint', 'Fix them in the catalog, then check again.')}</small></span><button type="button" className="fp-button fp-button--quiet" onClick={validate}>{t('facets.policy.number.revalidate', 'Check again')}</button></header>
              {(Array.isArray(result.invalid_examples) ? result.invalid_examples : []).slice(0, 10).map((example, index) => {
                const raw = example && typeof example === 'object'
                  ? (example.value ?? example.raw_value ?? JSON.stringify(example)) : example;
                const count = example && typeof example === 'object'
                  ? (example.products ?? example.product_count ?? null) : null;
                return <div key={`${String(raw)}-${index}`}><code>{raw == null || raw === '' ? t('facets.policy.number.emptyValue', 'Empty value') : String(raw)}</code>{count != null && <span>{t('facets.policy.number.productCount', '{count} products', { count })}</span>}</div>;
              })}
            </div>
          )}
          <div className="fp-validation-actions">
            <button type="button" className="fp-button fp-button--quiet" onClick={validate}>{t('facets.policy.number.revalidate', 'Check again')}</button>
            {reindexRequired && <button type="button" className="fp-button fp-button--primary" onClick={onRequestReindex}>{t('facets.policy.number.reindex', 'Start full reindex')}</button>}
          </div>
        </div>
      )}
    </section>
  );
}

NumberFacetPolicyEditor.propTypes = {
  api: PropTypes.shape({ validateType: PropTypes.func.isRequired }),
  facetKey: PropTypes.string.isRequired,
  draft: PropTypes.shape({ type: PropTypes.string, value_policy: PropTypes.object }).isRequired,
  effect: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onValidationChange: PropTypes.func,
  onRequestReindex: PropTypes.func,
};
NumberFacetPolicyEditor.defaultProps = { api: null, effect: null, onValidationChange: () => {}, onRequestReindex: () => {} };
